/**
 * Where an unauthenticated portal visitor goes, and where login sends them back.
 *
 * WHY THIS IS ONE MODULE. Before 2026-09-21 `/portal` had no answer for "no
 * session": `AdminRedirectGate` handled the admin-token case and let everything
 * else fall through to the dashboard, which 401-stormed and then rendered a bare
 * line of text — "Unable to load your portal. Please log in again." — with no
 * link and no redirect. Two sites now have to agree on the same vocabulary: the
 * gate that BUILDS the login href, and the login page that CONSUMES its `next`.
 * Splitting them is how a `next` the gate emits becomes one the login page
 * silently drops.
 *
 * Both functions are deliberately paranoid about `next`, because it is a
 * redirect target that arrives on the URL:
 *   - it must be a root-relative path (no scheme, no `//host`, no `\\host`),
 *   - it must land inside `/portal`, so the portal login cannot be used as an
 *     open redirect into the admin app or off-origin,
 *   - it must NOT be `/portal/login` itself, which is the redirect loop the
 *     product owner would hit next,
 *   - and it must contain no `..` segment, which is how `/portal/../../login`
 *     escapes the prefix check while still passing `startsWith('/portal')`.
 * Anything else resolves to the canonical post-login landing rather than
 * failing — a visitor with a mangled link still gets into their portal.
 */

/** The employee portal's own login route (`app/(portal)/portal/login/page.tsx`). */
export const PORTAL_LOGIN_PATH = '/portal/login';

/**
 * Where an ADMIN who opens a bare `/portal` is sent: the "View as employee"
 * preview (`app/settings/portal/employee/view-as/page.tsx`), which needs no
 * portal session. The gate named `/settings/portal?tab=view-as` until
 * 13 Oct 2026 — an address retired when the portal settings moved to their
 * own routes, so the admin landed on Theme. A test checks the page this names
 * exists.
 */
export const PORTAL_VIEW_AS_PATH = '/settings/portal/employee/view-as';

/**
 * Where sign-in lands a recipient when no usable `next` came along: the portal
 * home. Until 17 Oct 2026 this was three places for one sign-in - the code
 * landed on /portal/training, the magic link on /portal/training and single
 * sign-on on /portal - and a code sign-in scrolled past the home's banner. Every
 * way in now ends at the home, which is where the person's to-dos are.
 */
export const PORTAL_POST_LOGIN_DEFAULT = '/portal';

/** `/portal`, `/portal/…`, `/portal?…` — but never `/portal-settings`. */
const PORTAL_ROUTE = /^\/portal(?:[/?#]|$)/;

/**
 * `?ws=` is a workspace UUID and nothing else.
 *
 * VERIFIED IN A BROWSER, 2026-09-21, on the build this fix has not touched:
 * `/portal/login?ws=ws-demo` never renders the sign-in form. The (portal)
 * layout gates its children on `!!workspaceId && settingsLoading`, the public
 * settings call 400s on an id that is not a uuid, react-query keeps retrying,
 * and the visitor sits on "Loading portal…" for as long as they care to wait.
 * That is a pre-existing layout defect — but this redirect is what would now
 * DELIVER people into it, so a `ws` we cannot vouch for is not forwarded.
 */
const WORKSPACE_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Is `candidate` a root-relative path inside the employee portal that is safe
 * to hand to `router.push` / `router.replace`?
 */
/**
 * The sessionStorage key the `(portal)` layout resolves its workspace from
 * (`layout.tsx` WS_STORAGE_KEY). Declared here so the gate can purge a value
 * the shell cannot resolve without importing the layout.
 */
export const PORTAL_WS_STORAGE_KEY = 'hf-portal:ws-id';

/** Is this a workspace id the portal shell can actually resolve? */
export function isResolvableWorkspaceId(value: string | null | undefined): boolean {
  return typeof value === 'string' && WORKSPACE_ID.test(value);
}

export function isSafePortalPath(candidate: string): boolean {
  if (typeof candidate !== 'string' || candidate.length === 0) return false;
  if (!candidate.startsWith('/')) return false;
  // `//evil.example` is protocol-relative — the browser treats it as off-origin.
  if (candidate.startsWith('//')) return false;
  // A backslash is normalised to `/` by browsers, so `/\evil.example` is the
  // same attack wearing a different hat.
  if (candidate.includes('\\')) return false;
  // Whitespace / control characters are never legitimate here and are the
  // classic way to smuggle a scheme past a prefix check.
  if (/[\s\u0000-\u001f\u007f]/.test(candidate)) return false;
  if (!PORTAL_ROUTE.test(candidate)) return false;

  const pathOnly = candidate.split(/[?#]/)[0];
  // `/portal/../../login` starts with `/portal` and resolves somewhere else.
  if (pathOnly.split('/').includes('..')) return false;
  // So does `/portal/%2e%2e/admin`: a browser reads %2e as a dot, so the
  // router lands on `/admin` while the check above saw no `..` segment. An
  // encoded dot, slash or backslash has no business in a portal path (the
  // routes are uuids and slugs), so they are refused outright rather than
  // decoded and re-checked. (Only the path: a query value may hold any text.)
  if (/%2e|%2f|%5c/i.test(pathOnly)) return false;
  // The loop: login must never send anyone back to login.
  if (pathOnly === PORTAL_LOGIN_PATH || pathOnly.startsWith(`${PORTAL_LOGIN_PATH}/`)) {
    return false;
  }
  return true;
}

/** The query parameter that carries an admin preview tab's one-time claim. */
const PREVIEW_CLAIM_PARAM = 'pv';

/** Strip a leading `?` so callers can pass either shape. */
function normalizeSearch(search: string | null | undefined): string {
  if (!search) return '';
  return search.startsWith('?') ? search.slice(1) : search;
}

/**
 * The login href for a visitor who is standing on `pathname?search` without a
 * live portal session.
 *
 * `ws` is forwarded at the TOP level as well as inside `next`, because the login
 * page and the portal layout both theme off `?ws=` (`useWorkspaceId`) — without
 * it a deep link that carried a workspace would arrive at an unthemed login.
 */
export function buildPortalLoginHref(
  pathname: string | null | undefined,
  search?: string | null,
): string {
  const incoming = new URLSearchParams(normalizeSearch(search));
  // The one-time claim of an admin preview tab (lib/api/portal-auth.ts) is not
  // a place to come back to.
  incoming.delete(PREVIEW_CLAIM_PARAM);
  const ws = incoming.get('ws');
  const usableWs = ws && WORKSPACE_ID.test(ws) ? ws : null;
  // A ws we will not forward is also stripped from `next`: the portal layout
  // prefers the QUERY param over storage, so carrying it through would poison
  // the very page the visitor lands on once they have signed in.
  if (ws && !usableWs) incoming.delete('ws');

  const params = new URLSearchParams();
  if (usableWs) params.set('ws', usableWs);

  const currentSearch = incoming.toString();
  const target = `${pathname ?? ''}${currentSearch ? `?${currentSearch}` : ''}`;
  if (isSafePortalPath(target)) params.set('next', target);

  const qs = params.toString();
  return qs ? `${PORTAL_LOGIN_PATH}?${qs}` : PORTAL_LOGIN_PATH;
}

/**
 * The sign-in link for a page that was refused with a 401 (a survey, a policy,
 * a course, the library): `buildPortalLoginHref`, plus the workspace this tab
 * already knows when the address did not name one. Without it a survey link
 * opened from an e-mail sent the person to an unthemed sign-in that did not
 * know which organisation they belong to, and sent them home afterwards.
 */
export function portalSignInHref(
  pathname: string | null | undefined,
  search?: string | null,
): string {
  const href = buildPortalLoginHref(pathname, search);
  const queryAt = href.indexOf('?');
  const params = new URLSearchParams(queryAt === -1 ? '' : href.slice(queryAt + 1));
  if (!params.has('ws') && typeof window !== 'undefined') {
    let stored: string | null = null;
    try {
      stored = window.sessionStorage.getItem(PORTAL_WS_STORAGE_KEY);
    } catch {
      stored = null;
    }
    if (stored && WORKSPACE_ID.test(stored)) {
      const withWs = new URLSearchParams({ ws: stored });
      params.forEach((value, key) => withWs.set(key, value));
      return `${PORTAL_LOGIN_PATH}?${withWs.toString()}`;
    }
  }
  return href;
}

/**
 * The path the login page should navigate to after a successful sign-in.
 * Returns `PORTAL_POST_LOGIN_DEFAULT` for anything it will not vouch for.
 */
export function resolvePortalNextPath(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return PORTAL_POST_LOGIN_DEFAULT;
  return isSafePortalPath(raw) ? raw : PORTAL_POST_LOGIN_DEFAULT;
}

/**
 * Where a single sign-on comes back to. A sign-in with a code lands on `next`
 * straight from the login page; a sign-in through an identity provider leaves
 * the page and returns to /portal/auth/callback, which cannot see the login
 * page's URL, so the login page leaves the place in sessionStorage under this
 * key and the callback reads it back.
 */
export const PORTAL_RETURN_TO_KEY = 'hf-portal:return-to';

/**
 * Remember `raw` (the login page's `next`) for the callback, if - and only if -
 * the login page itself would honour it. Anything else, or no `next` at all,
 * CLEARS the key: a place an earlier visit left must not be opened by a later,
 * unrelated sign-in. Site data being blocked is not an error: the callback
 * then lands on /portal.
 */
export function rememberPortalReturnTo(raw: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    if (typeof raw === 'string' && isSafePortalPath(raw)) {
      window.sessionStorage.setItem(PORTAL_RETURN_TO_KEY, raw);
    } else {
      window.sessionStorage.removeItem(PORTAL_RETURN_TO_KEY);
    }
  } catch {
    // Blocked site data: nothing to remember with.
  }
}

/**
 * The remembered place, left where it is (the failure page that sends the person
 * back to sign in needs it for their next attempt). `null` when there is none or
 * it is not one the portal will vouch for.
 */
export function peekPortalReturnTo(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(PORTAL_RETURN_TO_KEY);
    return typeof stored === 'string' && isSafePortalPath(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * The remembered place, once: read, forgotten, and returned only if it is still
 * a path the portal will vouch for (it is read back from storage, which is
 * input as far as this page is concerned). `null` when there is none or it is
 * not one.
 */
export function takePortalReturnTo(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(PORTAL_RETURN_TO_KEY);
    window.sessionStorage.removeItem(PORTAL_RETURN_TO_KEY);
    return typeof stored === 'string' && isSafePortalPath(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Is there a LIVE portal session in this browser: a token that is there, decodes and has not expired? The ONE test
 * the home's gate (app/(portal)/portal/page.tsx: not live -> /portal/login) and the login page (live -> on to the
 * portal, 27 Oct 2026) both read, so the two pages can never send a person back and forth. A token that looks live
 * and that the API refuses is cleared by fetchPortalApi, so the next visit to the login page shows the form.
 */
export function isLivePortalSession(auth: { isAuthenticated(): boolean; getToken(): string | null }): boolean {
  return auth.isAuthenticated() && !isPortalTokenDeadLocally(auth.getToken());
}

/**
 * Is this portal access token unusable WITHOUT asking the server?
 *
 * WHY THIS EXISTS. `AdminRedirectGate` used to decide on the mere PRESENCE of
 * `hf-portal:token`, so a token that was missing, malformed or long expired
 * still let `PortalDashboardInner` mount — and its ~14 hooks then fired a
 * 401-storm before anything could redirect. Measured in a browser on
 * 2026-09-21: a dead token produced 18 unauthorized requests to /v1/portal/*
 * before the recovery landed, which is precisely the storm the gate's own
 * comment says it exists to prevent.
 *
 * A JWT carries its own expiry, so the common cases — no token, not a JWT,
 * already expired — are answerable locally, with no round trip and no storm.
 *
 * DELIBERATELY NOT A SECURITY CHECK. The signature is not verified here and
 * must never be: the server is the only authority on whether a token is valid.
 * This only decides whether it is worth SENDING. A token that is well-formed
 * and unexpired but revoked server-side still reaches the API, 401s, and is
 * recovered by the session-recovery effect in the dashboard — one storm in the
 * rare case rather than one in the common case.
 *
 * Fail OPEN: anything this cannot parse confidently is treated as usable, so a
 * future token format cannot lock every employee out of the portal. The cost of
 * a false "usable" is one redirect later; the cost of a false "dead" is a
 * sign-in loop for everyone.
 */
export function isPortalTokenDeadLocally(token: string | null | undefined): boolean {
  if (typeof token !== 'string' || token.trim() === '') return true;

  const segments = token.split('.');
  if (segments.length !== 3) return true; // not a JWT at all

  let payload: unknown;
  try {
    const raw = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = raw + '='.repeat((4 - (raw.length % 4)) % 4);
    payload = JSON.parse(
      decodeURIComponent(
        atob(padded)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      ),
    );
  } catch {
    return true; // three segments but the middle one is not a JWT payload
  }

  if (typeof payload !== 'object' || payload === null) return true;
  const exp = (payload as { exp?: unknown }).exp;
  // No `exp` claim: not something we will call dead — fail open.
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return false;
  // 30s of slack absorbs clock skew between this browser and the API, so a
  // token seconds from expiry is not thrown away on a fast machine.
  return exp * 1000 <= Date.now() + 30_000;
}

// ── The magic link's return address ───────────────────────────────────────────

/**
 * Where a magic link's return address is kept.
 *
 * The API builds the e-mailed link itself (AuthMailProcessor.handleMagicLink)
 * and its send-magic-link body takes an e-mail and a workspace, nothing else, so
 * `next` cannot ride in the link. The login page therefore leaves it here when
 * it asks for the link, and the verify page reads it back. localStorage, not
 * sessionStorage: the link opens in a NEW tab (the mail client's), which does
 * not share the login tab's sessionStorage. It is keyed to the e-mail address
 * and expires with the link's own lifetime plus a margin.
 */
export const PORTAL_MAGIC_LINK_RETURN_KEY = 'hf-portal:magic-link-return';
const MAGIC_LINK_RETURN_MAX_AGE_MS = 60 * 60 * 1000;

export interface MagicLinkReturn {
  /** A path the portal will vouch for, or null. */
  next: string | null;
  /** A workspace uuid the portal shell can resolve, or null. */
  ws: string | null;
}

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

/**
 * Remember where the person was going, for the link about to be e-mailed to
 * `email`. Only a `next` the login page would honour, and only a workspace the
 * shell can resolve, are kept. Blocked site data is not an error: the link then
 * lands on the portal home.
 */
export function rememberMagicLinkReturn(
  email: string,
  next: string | null | undefined,
  ws: string | null | undefined,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      PORTAL_MAGIC_LINK_RETURN_KEY,
      JSON.stringify({
        email: normalizeEmail(email),
        next: typeof next === 'string' && isSafePortalPath(next) ? next : null,
        ws: typeof ws === 'string' && WORKSPACE_ID.test(ws) ? ws : null,
        at: Date.now(),
      }),
    );
  } catch {
    // Blocked site data: nothing to remember with.
  }
}

function readMagicLinkReturn(email: string, spend: boolean): MagicLinkReturn | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PORTAL_MAGIC_LINK_RETURN_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as {
      email?: unknown;
      next?: unknown;
      ws?: unknown;
      at?: unknown;
    };
    const age = typeof stored.at === 'number' ? Date.now() - stored.at : Number.POSITIVE_INFINITY;
    if (!(age >= 0 && age <= MAGIC_LINK_RETURN_MAX_AGE_MS)) {
      window.localStorage.removeItem(PORTAL_MAGIC_LINK_RETURN_KEY);
      return null;
    }
    if (typeof stored.email !== 'string' || stored.email !== normalizeEmail(email)) return null;
    if (spend) window.localStorage.removeItem(PORTAL_MAGIC_LINK_RETURN_KEY);
    // Read back from storage, so input as far as this page is concerned.
    return {
      next: typeof stored.next === 'string' && isSafePortalPath(stored.next) ? stored.next : null,
      ws: typeof stored.ws === 'string' && WORKSPACE_ID.test(stored.ws) ? stored.ws : null,
    };
  } catch {
    return null;
  }
}

/** The remembered return address for `email`, left in place (the "send a new link" path needs it). */
export function peekMagicLinkReturn(email: string): MagicLinkReturn | null {
  return readMagicLinkReturn(email, false);
}

/** The remembered return address for `email`, once: it is forgotten as it is read. */
export function takeMagicLinkReturn(email: string): MagicLinkReturn | null {
  return readMagicLinkReturn(email, true);
}

/** Forget it (the link could not be sent, so nothing was asked for). */
export function forgetMagicLinkReturn(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PORTAL_MAGIC_LINK_RETURN_KEY);
  } catch {
    // Nothing to forget with.
  }
}
